import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { BusinessCard, EventCard, OfferCard, ArticleCard, TourCard } from "@/components/Cards";
import { EXPLORE_SECTIONS, HERO_MURAL } from "@/lib/constants";

function Section({ title, viewAll, viewAllLabel, children, testid }) {
  return (
    <section className="mb-16" data-testid={testid}>
      <div className="flex items-end justify-between mb-6 border-b border-white/10 pb-3">
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-white">{title}</h2>
        {viewAll && (
          <Link to={viewAll} className="group flex items-center gap-1.5 text-sm font-semibold text-[#00E5FF] hover:text-white transition-colors" data-testid={`${testid}-viewall`}>
            {viewAllLabel || "View all"} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

function FeaturedEditorialHero({ items }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items.length]);
  if (!items.length) return null;
  const cur = items[idx % items.length];
  return (
    <Link to={`/guides/${cur.slug}`} data-testid="hub-featured-editorial"
      className="group relative block h-[360px] sm:h-[460px] overflow-hidden border border-white/10 mb-8">
      {items.map((g, i) => (
        <img key={g.id} src={g.image} alt={g.title}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out ${i === idx ? "opacity-100" : "opacity-0"} group-hover:scale-105`}
          style={{ transitionProperty: "opacity, transform" }} />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/45 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/75 via-transparent to-transparent" />
      <div className="absolute top-5 left-5">
        <span className="inline-block bg-[#FF0055] text-white text-[11px] font-accent tracking-wider px-3 py-1">FEATURED EDITORIAL</span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
        <span className="font-accent text-[#00E5FF] text-xs tracking-wider">{cur.category}</span>
        <h3 className="font-display font-black text-3xl sm:text-5xl text-white max-w-3xl leading-[1.05] mt-2 group-hover:text-[#FF0055] transition-colors">{cur.title}</h3>
        <p className="text-neutral-300 text-sm sm:text-base mt-3 max-w-2xl line-clamp-2">{cur.excerpt}</p>
        <div className="flex items-center gap-3 mt-4 text-xs text-neutral-400">
          <span className="text-white font-medium">{cur.author}</span><span>·</span><span>{cur.read_time}</span>
          <span className="ml-2 flex items-center gap-1 text-[#00E5FF] font-semibold">Read the guide <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
        </div>
      </div>
      {items.length > 1 && (
        <div className="absolute top-5 right-5 flex gap-1.5" data-testid="hub-featured-dots">
          {items.map((_, i) => (
            <button key={i} onClick={(e) => { e.preventDefault(); setIdx(i); }}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#FFEA00]" : "w-1.5 bg-white/40 hover:bg-white/70"}`} aria-label={`Featured story ${i + 1}`} />
          ))}
        </div>
      )}
    </Link>
  );
}

export default function CategoryHub() {
  const { section } = useParams();
  const cfg = EXPLORE_SECTIONS[section];

  const [venues, setVenues] = useState([]);
  const [guides, setGuides] = useState([]);
  const [moreStories, setMoreStories] = useState([]);
  const [offers, setOffers] = useState([]);
  const [events, setEvents] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cfg) return;
    setLoading(true);
    window.scrollTo(0, 0);
    const bizReq = cfg.business.type === "category"
      ? api.get("/businesses", { params: { category: cfg.business.value } })
      : api.get("/businesses");

    Promise.all([
      bizReq,
      api.get("/articles"),
      api.get("/offers"),
      api.get("/events"),
      api.get("/tours"),
    ]).then(([b, a, o, e, t]) => {
      let bz = b.data;
      if (cfg.business.type === "tags") {
        const want = cfg.business.value;
        bz = bz.filter((x) => (x.tags || []).some((tag) => want.includes(tag)));
      }
      // real photos first for a stronger visual hero
      bz.sort((x, y) => (x.photo_source !== "website") - (y.photo_source !== "website") || (!x.featured) - (!y.featured));
      setVenues(bz);

      const gs = cfg.guideSlugs || [];
      const gt = cfg.guideTags || [];
      const featured = a.data.filter((art) =>
        gs.includes(art.slug) || (art.tags || []).some((tag) => gt.includes(tag))
      );
      setGuides(featured);
      const fset = new Set(featured.map((g) => g.slug));
      setMoreStories(
        a.data.filter((art) => art.published !== false && !fset.has(art.slug))
          .sort((x, y) => (y.created_at || "").localeCompare(x.created_at || ""))
          .slice(0, 3)
      );

      setOffers(o.data.filter((of) => (cfg.offerCats || []).includes(of.category)));
      const now = new Date().toISOString().slice(0, 10);
      setEvents(e.data.filter((ev) => !ev.date || ev.date >= now).slice(0, 4));
      setTours(t.data.slice(0, 3));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [section]); // eslint-disable-line

  if (!cfg) return <Navigate to="/directory" replace />;

  const heroImg = venues.find((v) => v.photo_source === "website")?.image || HERO_MURAL;
  const sectionParam = section;

  return (
    <div>
      <Seo title={cfg.title} description={cfg.subtitle} image={heroImg} canonicalPath={`/explore/${section}`} />
      <PageHeader overline={cfg.overline} title={cfg.title} subtitle={cfg.subtitle} image={heroImg} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        {loading ? (
          <div className="text-neutral-500 py-24 text-center">Loading RiNo...</div>
        ) : (
          <>
            {/* Guides & Stories — magazine featured editorial rotation first */}
            {(guides.length > 0 || moreStories.length > 0) && (
              <Section title="Guides & Stories" viewAll="/guides" viewAllLabel="All guides" testid="hub-guides">
                <FeaturedEditorialHero items={(guides.length ? guides : moreStories).slice(0, 5)} />
                {moreStories.length > 0 && (
                  <>
                    <h3 className="font-accent text-[#00E5FF] text-sm mb-4">More stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {moreStories.map((a, i) => <ArticleCard key={a.id} a={a} index={i} />)}
                    </div>
                  </>
                )}
              </Section>
            )}

            {/* Deals & Offers */}
            {offers.length > 0 && (
              <Section title="Deals & Offers" viewAll="/offers" viewAllLabel="All deals" testid="hub-deals">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {offers.slice(0, 3).map((o, i) => <OfferCard key={o.id} o={o} index={i} />)}
                </div>
              </Section>
            )}

            {/* Tours & Experiences */}
            {tours.length > 0 && (
              <Section title="Tours & Experiences" viewAll="/tours" viewAllLabel="All tours" testid="hub-tours">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {tours.map((t, i) => <TourCard key={t.id} t={t} index={i} />)}
                </div>
              </Section>
            )}

            {/* Venues */}
            <Section title={`Venues (${venues.length})`} viewAll={`/directory?section=${sectionParam}`} viewAllLabel="View all in Directory" testid="hub-venues">
              {venues.length === 0 ? (
                <div className="text-neutral-500 py-10 text-center border border-white/10">No venues here yet — check back soon.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {venues.slice(0, 9).map((b, i) => <BusinessCard key={b.id} b={b} index={i} />)}
                </div>
              )}
            </Section>

            {/* What's On (events) */}
            {events.length > 0 && (
              <Section title="What's On" viewAll="/events" viewAllLabel="All events" testid="hub-events">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {events.map((e, i) => <EventCard key={e.id} e={e} index={i} />)}
                </div>
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
